/* ============================================================================
   CONTOURNEMENT DES FLÈCHES — géométrie pure, partagée RENDU ⟷ BANC
   ----------------------------------------------------------------------------
   Le défaut le plus fréquent du banc de lisibilité : une flèche ordonnée passe
   EN PLEIN SUR la pastille d'un joueur étranger à l'action. Mesuré sur le
   corpus (1106 scènes) : 151 occurrences sur 125 scènes, tous sports, moitié
   route analyse / moitié route gabarit — ce n'est donc pas un gabarit fautif,
   c'est un défaut de TRACÉ, transversal.

   La réponse est de COURBER la flèche autour du pion, jamais de déplacer le
   pion : sa position porte l'information tactique, la ligne droite non.
   La courbe reste MINIMALE (le plus petit écart qui dégage, médiane mesurée
   ~3,5 unités) : une passe contournée reste lue comme une passe.

   Deux invariants tiennent tout le montage :

   1. ON NE CUIT RIEN DANS LA SCÈNE. Le contournement se calcule AU RENDU, à
      partir des positions courantes — comme `deconflictBallSegments`. Un
      `path` mémorisé dans la scène resterait sur l'ancienne courbe dès que
      l'éditeur déplace un joueur (`src/editor.js`), et la flèche se
      détacherait du pion qu'on vient de bouger. Les `path` AUTEURS (le relais
      d'athlétisme, qui décrit la piste) sont, eux, laissés intacts.

   2. UNE SEULE SOURCE DE VÉRITÉ. `arrowDetour()` est appelée par `render.js`
      (ce qui est dessiné) ET par `src/legibility.js` (ce qui est mesuré) :
      le banc ne peut pas mesurer une géométrie que le rendu ne dessine pas.
      Quand aucun contournement acceptable n'existe — il faudrait sortir du
      terrain, ou traverser quelqu'un d'autre — la fonction renvoie `null` et
      le défaut RESTE signalé. Le banc ne ment pas par construction.
   ========================================================================== */

/* Seuils du banc de lisibilité, repris à l'identique : une flèche « traverse »
   un pion s'il est à moins de CROSS_CLEAR du tracé, ailleurs qu'aux abords des
   extrémités (une flèche PART ou ARRIVE naturellement près de quelqu'un). */
export const CROSS_CLEAR = 2.5;
export const CROSS_TMIN = 0.12;
export const CROSS_TMAX = 0.88;

/* Types de flèche contournables. Exclus : `rotation` (le rendu lui applique
   déjà son propre arc, `drawRotationArc`, qui ignore `path`) et `ecran` (sa
   barre perpendiculaire de fin n'est dessinée que par la flèche droite). */
const DETOURABLE = new Set(["passe", "centre", "renvoi", "tir", "course", "dribble"]);
/* Gestes dont la traversée d'un ADVERSAIRE est le geste décrit lui-même (tir
   devant le gardien, fixation, course « dans le dos ») — même exemption que
   le banc, définie ICI pour que les deux ne puissent pas diverger. */
const THROUGH_OPPONENT_OK = new Set(["tir", "course", "dribble"]);

const MIN_LENGTH = 6;      // en deçà, une flèche n'a pas de place pour tourner
const MAX_OFFSET = 12;     // écart latéral maximal : au-delà, ce n'est plus la même passe
const OFFSET_STEP = 0.5;
const MARGIN = 0.6;        // dégagement visé au-delà du seuil du banc
const SAMPLES = 14;        // échantillonnage de la courbe en polyligne
const BOUND = 1;           // marge de terrain (les coordonnées vivent sur 0..100)

/* Distance d'un point à un SEGMENT + abscisse relative du pied de la
   perpendiculaire (0 au départ, 1 à l'arrivée). */
export function segmentDistance(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const L2 = dx * dx + dy * dy;
  if (L2 < 1e-6) return { d: Math.hypot(px - ax, py - ay), t: 0 };
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L2));
  return { d: Math.hypot(px - (ax + t * dx), py - (ay + t * dy)), t };
}

/* Idem pour une POLYLIGNE : `t` est l'abscisse CURVILIGNE (fraction de la
   longueur développée), pour que les mêmes seuils d'extrémités s'appliquent. */
export function polylineDistance(px, py, path) {
  let total = 0;
  for (let i = 1; i < path.length; i++) total += Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
  let best = Infinity, bt = 0, acc = 0;
  for (let i = 1; i < path.length; i++) {
    const L = Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
    const r = segmentDistance(px, py, path[i - 1].x, path[i - 1].y, path[i].x, path[i].y);
    if (r.d < best) { best = r.d; bt = total > 0 ? (acc + r.t * L) / total : 0; }
    acc += L;
  }
  return { d: best, t: bt };
}

/* Distance au tracé RÉELLEMENT dessiné : la polyligne si elle existe, le
   segment sinon. C'est la primitive que le banc doit utiliser. */
export function arrowDistance(px, py, from, to, path) {
  return (Array.isArray(path) && path.length >= 2)
    ? polylineDistance(px, py, path)
    : segmentDistance(px, py, from.x, from.y, to.x, to.y);
}

/* « Ce pion est-il traversé par ce tracé ? » — seuils du banc. */
export function crossesArrow(px, py, from, to, path) {
  const r = arrowDistance(px, py, from, to, path);
  return r.d < CROSS_CLEAR && r.t > CROSS_TMIN && r.t < CROSS_TMAX;
}

/* Courbe de Bézier quadratique de A à B, écartée de `off` du côté `side`,
   échantillonnée en polyligne (l'écart maximal, au milieu, vaut `off`). */
function bend(A, B, off, side, samples = SAMPLES) {
  const dx = B.x - A.x, dy = B.y - A.y, L = Math.hypot(dx, dy) || 1;
  const nx = -dy / L, ny = dx / L;
  const cx = (A.x + B.x) / 2 + nx * side * off * 2;
  const cy = (A.y + B.y) / 2 + ny * side * off * 2;
  const out = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples, u = 1 - t;
    out.push({ x: u * u * A.x + 2 * u * t * cx + t * t * B.x,
      y: u * u * A.y + 2 * u * t * cy + t * t * B.y });
  }
  return out;
}

/* L'ARC DE ROTATION, échantillonné. Une flèche de rotation (passage d'un
   atelier au suivant) n'est jamais dessinée droite : `drawRotationArc` la
   courbe sur le côté, par un point de contrôle décalé de `bulge`. Le banc
   mesurait pourtant le segment — donc une géométrie que le rendu ne dessine
   pas, et il signalait des traversées qui n'existent pas à l'écran. Le tracé
   reproduit ici est CELUI DU RENDU, clamp compris. */
export function rotationArcPath(from, to, bulge = 7, samples = SAMPLES) {
  if (!from || !to) return null;
  if (!Number.isFinite(from.x) || !Number.isFinite(from.y) || !Number.isFinite(to.x) || !Number.isFinite(to.y)) return null;
  const cx = Math.min(99, Math.max(from.x, to.x) + bulge), cy = (from.y + to.y) / 2;
  const out = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples, u = 1 - t;
    out.push({ x: u * u * from.x + 2 * u * t * cx + t * t * to.x,
      y: u * u * from.y + 2 * u * t * cy + t * t * to.y });
  }
  return out;
}

/* Les pions que CETTE flèche ne doit pas traverser, à l'instant de l'action.
   `pos` porte les positions COURANTES (une course déplace son acteur) ; la
   position INITIALE compte aussi, car c'est là qu'est dessinée la pastille.
   Départ et arrivée de la flèche sont évidemment exclus. */
export function arrowBlockers(action, players, pos) {
  const actor = players.find((p) => p.id === action.from);
  const out = [];
  for (const p of players) {
    if (p.id === action.from || p.id === action.to) continue;
    if (THROUGH_OPPONENT_OK.has(action.type) && actor && (p.team || "") !== (actor.team || "")) continue;
    const cur = pos[p.id];
    const start = { x: +p.x, y: +p.y };
    if (Number.isFinite(start.x) && Number.isFinite(start.y)) out.push(start);
    if (cur && Number.isFinite(cur.x) && Number.isFinite(cur.y)
      && Math.hypot(cur.x - start.x, cur.y - start.y) > 0.01) out.push({ x: cur.x, y: cur.y });
  }
  return out;
}

/* LE CONTOURNEMENT. Renvoie la polyligne à dessiner, ou `null` — soit qu'il
   n'y ait rien à contourner, soit qu'aucune courbe acceptable n'existe (le
   défaut reste alors signalé par le banc, ce qui est le comportement voulu).
   On essaie d'abord le côté qui S'ÉLOIGNE du pion fautif, puis l'autre, avec
   l'écart le plus petit qui dégage — la flèche doit rester lisible comme le
   geste qu'elle décrit. */
export function detourPath(from, to, blockers, opts = {}) {
  const { clear = CROSS_CLEAR, margin = MARGIN, maxOffset = MAX_OFFSET, samples = SAMPLES } = opts;
  if (!from || !to || !Array.isArray(blockers) || !blockers.length) return null;
  if (!Number.isFinite(from.x) || !Number.isFinite(from.y) || !Number.isFinite(to.x) || !Number.isFinite(to.y)) return null;
  const dx = to.x - from.x, dy = to.y - from.y;
  if (Math.hypot(dx, dy) < MIN_LENGTH) return null;

  const hit = (path) => blockers.find((o) => {
    const r = path ? polylineDistance(o.x, o.y, path) : segmentDistance(o.x, o.y, from.x, from.y, to.x, to.y);
    return r.d < clear && r.t > CROSS_TMIN && r.t < CROSS_TMAX;
  });
  const guilty = hit(null);
  if (!guilty) return null;

  // Côté du pion fautif : on courbe à l'OPPOSÉ en premier.
  const away = Math.sign((guilty.x - from.x) * dy - (guilty.y - from.y) * dx) || 1;
  for (const side of [away, -away]) {
    for (let off = clear + margin; off <= maxOffset + 1e-9; off += OFFSET_STEP) {
      const path = bend(from, to, off, side, samples);
      // Sortir du terrain n'est pas un contournement : on abandonne ce côté.
      if (path.some((q) => q.x < BOUND || q.x > 100 - BOUND || q.y < BOUND || q.y > 100 - BOUND)) break;
      if (!hit(path)) return path;
    }
  }
  return null;
}

/* Le point d'entrée des deux consommateurs : la courbe d'évitement de CETTE
   action, ou `null` si elle doit rester droite. `from`/`to` sont les extrémités
   déjà résolues (positions courantes), `pos` la table des positions à cet
   instant de la chronologie. */
export function arrowDetour(action, from, to, players, pos, opts) {
  if (!action) return null;
  // La rotation n'est pas contournée : elle est DÉJÀ courbe. On rend son arc
  // pour que la mesure porte sur le bon tracé.
  if (action.type === "rotation") return rotationArcPath(from, to, action.bulge || 7);
  if (!DETOURABLE.has(action.type)) return null;
  return detourPath(from, to, arrowBlockers(action, players, pos), opts);
}
